import { useQuery } from '@tanstack/react-query';

import { useXFetch } from '~/shared/utils/useXFetch';

const baseUrl = '/api/v1/volumes/autoscaling/rules';

export interface AutoscaleRuleDetailsFromApi {
  rule_id: string;
  active: boolean;
  rule: Record<string, unknown>;
  matched_volumes_count?: number;
  total_capacity?: number;
  avg_utilization?: number;
  exceptions_count?: number;
}

export interface AutoscaleRuleDetails {
  ruleId: string;
  enabled: boolean;
  volumes: number;
  capacity: number;
  utilization: string;
  exceptions: number;
  rawRule: Record<string, unknown>;
}

/**
 * Transform API response to frontend format
 * temporary till the backend is updated
 */
function transformApiRuleDetailsToFrontend(
  apiRule: AutoscaleRuleDetailsFromApi
): AutoscaleRuleDetails {
  return {
    ruleId: apiRule.rule_id,
    enabled: apiRule.active,
    volumes: apiRule.matched_volumes_count || 0,
    capacity: apiRule.total_capacity || 0,
    utilization: apiRule.avg_utilization ? `${Math.round(apiRule.avg_utilization)}%` : '0%',
    exceptions: apiRule.exceptions_count || 0,
    rawRule: apiRule.rule,
  };
}

/**
 * Hook to fetch details for a specific autoscaling rule
 */
export function useAutoscaleRuleDetails(ruleId: string | null, enabled: boolean = true) {
  const api = useXFetch();

  return useQuery<AutoscaleRuleDetails | null>({
    queryKey: ['autoscaleRuleDetails', ruleId],
    enabled: enabled && !!ruleId,
    refetchOnWindowFocus: false,
    staleTime: 0,
    queryFn: async () => {
      if (!ruleId) {
        return null;
      }

      const response = await api<AutoscaleRuleDetailsFromApi>(`${baseUrl}/${ruleId}`);

      const transformed = transformApiRuleDetailsToFrontend(response);

      return transformed;
    },
  });
}
